import { useEffect, useId, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (element: string | HTMLElement, options: Record<string, unknown>) => string;
      remove: (id: string) => void;
      reset: (id: string) => void;
    };
  }
}

export function TurnstileWidget({ onToken }: { onToken: (token: string) => void }) {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const id = `turnstile-${useId().replace(/:/g, '')}`;
  const widget = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!siteKey) return;

    const render = () => {
      if (window.turnstile && !widget.current) {
        widget.current = window.turnstile.render(`#${id}`, {
          sitekey: siteKey,
          callback: onToken,
          'expired-callback': () => onToken(''),
          'error-callback': () => onToken(''),
          theme: 'light',
        });
      }
    };

    if (window.turnstile) {
      render();
      return;
    }

    let script = document.querySelector<HTMLScriptElement>('script[data-turnstile]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.turnstile = 'true';
      document.head.appendChild(script);
    }

    script.addEventListener('load', render, { once: true });
    return () => script?.removeEventListener('load', render);
  }, [id, onToken, siteKey]);

  useEffect(
    () => () => {
      if (widget.current) window.turnstile?.remove(widget.current);
    },
    [],
  );

  if (!siteKey) return null;
  return (
    <div className="turnstile-wrap">
      <div id={id} />
    </div>
  );
}
